import { AsyncPipe, NgComponentOutlet } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DOCUMENT,
    DestroyRef,
    HostBinding,
    NgZone,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot } from '@angular/router';

import { BetslipConfig, EpcotConfig, Sitecore, SkeletonConfig, StakeConfig } from '@frontend/sports/common/client-config-data-access';
import { HideHeaderService } from '@frontend/sports/common/core/feature/hide-header';
import { ScrollFreezeService } from '@frontend/sports/common/core/feature/scroll-freeze';
import { equalParamsAndUrlSegments } from '@frontend/sports/common/core/utils/router';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { ComponentProxy, ComponentProxyDirective } from '@frontend/sports/deferred/feature/component-loader';
import { AutoCloseSwipeDirective } from '@frontend/sports/gesture/feature';
import { AdaptiveLayoutService, ColumnComponent, ColumnLayoutService, LayoutColumn, SlotLayoutService } from '@frontend/sports/layout/feature';
import { ToasterService } from '@frontend/sports/toaster/feature';
import { GeneralTooltipComponent } from '@frontend/sports/tooltip-feature';
import { UserService } from '@frontend/sports/user/feature';
import { WidgetColumnComponent } from '@frontend/sports/widget/feature/column';
import { DeviceService, HtmlNode, ProductHomepagesConfig, WINDOW } from '@frontend/vanilla/core';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import { difference } from 'lodash-es';
import { NgScrollbar, NgScrollbarModule } from 'ngx-scrollbar';
import { Observable, defer, distinctUntilKeyChanged, map, merge, of, pairwise, startWith, switchMap } from 'rxjs';

import { BetslipIntegrationService } from '../../betslip/integration/betslip-integration.service';
import {
    BetslipDigitalComponentLoaderService,
    EditMyBetAddPicksContainerMobileComponentBinding,
} from '../betslip/betslip-digital-component-loader.service';
import { PrintLoadingIndicatorComponent } from '../print/print-loading-indicator.component';
import { SkeletonPage } from '../skeleton-ui/skeleton-page.model';
import { SkeletonSportsBaseComponent } from '../skeleton-ui/skeleton-sports-base.component';
import { SkeletonService } from '../skeleton-ui/skeleton.service';
import { UiManagerComponent } from '../ui-manager/ui-manager.component';
import { UpdateMobileNumberService } from '../update-mobile-number/update-mobile-number.service';
import { MainColumnComponent } from './main-column.component';

@Component({
    selector: 'ms-main',
    templateUrl: './main.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ComponentProxyDirective,
        GeneralTooltipComponent,
        NgScrollbarModule,
        MainColumnComponent,
        AsyncPipe,
        PrintLoadingIndicatorComponent,
        NgComponentOutlet,
        UiManagerComponent,
        ColumnComponent,
        SkeletonSportsBaseComponent,
        WidgetColumnComponent,
        AutoCloseSwipeDirective,
    ],
})
export class MainComponent implements AfterViewInit, OnDestroy, OnInit {
    betslipConfig = inject(BetslipConfig);
    scrollFreeze = inject(ScrollFreezeService);
    columnLayout = inject(ColumnLayoutService);
    device = inject(DeviceService);
    private scrollContainer = inject(ScrollContainerService);
    private slotLayout = inject(SlotLayoutService);
    private html = inject(HtmlNode);
    private betslipLoader = inject(BetslipDigitalComponentLoaderService);
    private userService = inject(UserService);
    private siteCore = inject(Sitecore);
    private productHomepagesConfig = inject(ProductHomepagesConfig);
    private stakeConfig = inject(StakeConfig);
    private epcotConfig = inject(EpcotConfig);
    private changeRef = inject(ChangeDetectorRef);
    private skeletonService = inject(SkeletonService);
    skeletonConfig = inject(SkeletonConfig);
    private rxStrategy = inject(RxStrategyProvider);
    private ngZone = inject(NgZone);
    private updateMobileNumberService = inject(UpdateMobileNumberService);

    private MESSAGES_COLLAPSE_THRESHOLD = 75;
    private MESSAGES_COLLAPSE_CLASS = 'content-messages-hidden';
    private readonly _window = inject(WINDOW);

    column = LayoutColumn;
    useCustomScrollbar: boolean;
    useCustomLayout: boolean;
    disableScroll = false;
    skeletonPage: SkeletonPage | undefined;
    @HostBinding('class') className = 'main-container ds-scrollbar';

    @HostBinding('class.epcot-enabled')
    get epcotEnabledClass() {
        return this.isEpcotEnabled;
    }

    @ViewChildren(NgScrollbar) columnRef: QueryList<NgScrollbar>;
    @ViewChild('centerColumn', { read: NgScrollbar }) centerRef: NgScrollbar;

    private htmlClasses: string[] = [];
    quickBetComponent: ComponentProxy;
    readonly showQuickBet$: Observable<boolean>;
    readonly showQuickBetBuilder$: Observable<boolean>;
    betslipReturnsMessageComponent: ComponentProxy;
    editBetAddPicksContainerMobileComponent: ComponentProxy<EditMyBetAddPicksContainerMobileComponentBinding>;
    isEpcotEnabled = false;
    private readonly _doc = inject(DOCUMENT);
    private readonly destroyRef = inject(DestroyRef);
    printContainerComponent$ = defer(() => import('../print/print-container.component')).pipe(map((i) => i.PrintContainerComponent));

    constructor() {
        const device = this.device;
        const adaptiveLayout = inject(AdaptiveLayoutService);
        const toaster = inject(ToasterService);
        const routerEvent = inject(RouterEventsService);
        const betslipIntegrationService = inject(BetslipIntegrationService);
        const skeletonConfig = this.skeletonConfig;

        inject(HideHeaderService);

        this.userService.onUserLogInWithSettings$.pipe(takeUntilDestroyed()).subscribe(() => {
            if (this.updateMobileNumberService.showMobileNumberConfirmation()) {
                this.updateMobileNumberService.launchMobileNumberConfirmationPopup();
            }
            this.checkStakeLimit();
            this.rxStrategy.scheduleCD(this.changeRef, { scope: this, patchZone: this.ngZone });
        });

        if (skeletonConfig.isEnabled && !device.isRobot) {
            this.skeletonService.skeletonPage$?.pipe(takeUntilDestroyed()).subscribe((page: SkeletonPage | undefined) => {
                if (page !== SkeletonPage.Live) {
                    this.skeletonPage = page;
                    this.rxStrategy.scheduleCD(this.changeRef, { scope: this, patchZone: this.ngZone });
                }
            });
        }

        this.quickBetComponent = this.betslipLoader.getQuickBetComponent();
        this.showQuickBet$ = betslipIntegrationService.shouldShowQuickBet$();
        this.showQuickBetBuilder$ = betslipIntegrationService.shouldShowQuickBetBuilder$();
        this.betslipReturnsMessageComponent = this.betslipLoader.getBetslipReturnsMessageComponent();

        this.editBetAddPicksContainerMobileComponent = this.betslipLoader.getEditBetAddPicksContainerMobileComponent();
        this.editBetAddPicksContainerMobileComponent.subscribeToOutputs({ picksContainerBottomOffset: this.picksContainerOffsetChanged });

        this.html.setCssClass('product-sports', true);
        this.html.setCssClass('header-sticky', false);
        this.html.setCssClass('event-details-page', false);
        this.html.setCssClass('horse-racing', false);

        adaptiveLayout.stateChange$.pipe(distinctUntilKeyChanged('htmlClasses'), takeUntilDestroyed()).subscribe((state) => {
            //  distinct htmlClasses means that also the cardContent flag changed as it is used to calculate the classes
            this.useCustomScrollbar = !!state.cardContent && !device.isMobile && !device.isRobot;
            this.useCustomLayout = !!state.cardContent;

            this.html.setCssClass('scrollable-content', this.useCustomScrollbar);

            this.setScrollEventTarget();
            this.setCssClasses(state.htmlClasses);
            this.rxStrategy.scheduleCD(this.changeRef, { scope: this, patchZone: this.ngZone });
        });

        routerEvent.currentActivationStart.pipe(takeUntilDestroyed()).subscribe((route) => {
            this.disableScroll = route?.snapshot.data?.disableScroll;
            toaster.dismiss();
            this.rxStrategy.scheduleCD(this.changeRef, { scope: this, patchZone: this.ngZone });
        });

        routerEvent.currentActivationEnd
            .pipe(startWith(undefined), pairwise(), takeUntilDestroyed())
            .subscribe(([previousActivationEnd, currentActivationEnd]) => {
                if (!previousActivationEnd) {
                    return;
                }

                if (this.isPopupNavigation(previousActivationEnd.snapshot, currentActivationEnd!.snapshot)) {
                    return;
                }

                const navigationHappened = !equalParamsAndUrlSegments(previousActivationEnd.snapshot, currentActivationEnd!.snapshot);
                if (navigationHappened) {
                    requestAnimationFrame(() => this.scrollContainer.scrollTo(0, 0));
                }
            });

        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    get isScrollFrozen(): boolean {
        return this.scrollFreeze.isScrollFrozen;
    }

    ngOnInit(): void {
        /** Coming back from the login will leave the components not swaped back to the inner column so we double check the layout si correct*/
        this.slotLayout.swapComponents();
        this.checkStakeLimit();
    }

    ngOnDestroy(): void {
        this.slotLayout.restore();
        this.html.setCssClass('product-sports', false);
        this.html.setCssClass('scrollable-content', false);
    }

    ngAfterViewInit(): void {
        this.setScrollEventTarget();

        this.columnLayout.resetMeasurement();

        merge(of(undefined), this.columnRef.changes)
            .pipe(
                map(() => this.columnRef.map((scrollbar) => scrollbar.scrolled)),
                switchMap((events) => merge(...events)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((event) => {
                this.scrolled(event);
                this.rxStrategy.scheduleCD(this.changeRef, { scope: this, patchZone: this.ngZone });
            });
    }

    private hasPopup(routeSnapshot: ActivatedRouteSnapshot | undefined) {
        return Boolean(routeSnapshot?.queryParams.popup);
    }

    private isPopupNavigation(
        previousActivation: ActivatedRouteSnapshot | undefined,
        currentActivation: ActivatedRouteSnapshot | undefined,
    ): boolean {
        return this.hasPopup(currentActivation) || this.hasPopup(previousActivation);
    }

    private scrolled(event: Event): void {
        if (event.target instanceof Element) {
            const messagesVisible = event.target.scrollTop < this.MESSAGES_COLLAPSE_THRESHOLD;

            if (this.html.hasCssClass(this.MESSAGES_COLLAPSE_CLASS) === messagesVisible) {
                this.html.setCssClass(this.MESSAGES_COLLAPSE_CLASS, !messagesVisible);
            }
        }
    }

    private picksContainerOffsetChanged = (bottomOffset: number): void => {
        if (this.centerRef) {
            const content = this.centerRef.viewport.nativeElement.firstChild;

            if (content instanceof HTMLElement) {
                content.style.paddingBottom = `${bottomOffset}px`;
            }
        }
    };

    private setScrollEventTarget(): void {
        if (this.useCustomLayout && this.centerRef) {
            this.scrollContainer.setTarget(this.centerRef.viewport?.nativeElement);
        } else {
            this.scrollContainer.setTarget(this._doc.scrollingElement || this._window, this._window);
        }
    }

    private setCssClasses(cssClasses: string[]): void {
        const toRemove = difference(this.htmlClasses, cssClasses);
        toRemove.forEach((htmlClass) => this.html.setCssClass(htmlClass, false));

        const toAdd = difference(cssClasses, this.htmlClasses);
        toAdd.forEach((htmlClass) => this.html.setCssClass(htmlClass, true));

        this.htmlClasses = cssClasses;
    }

    private checkStakeLimit(): void {
        if (this.userService.isAuthenticated && this.stakeConfig.checkUserStakeLimit && !this.userService.getIsStakeLimitSet()) {
            this._window.location.href = this.productHomepagesConfig.portal + this.siteCore.globalMessages.StakeLimitUrl;
        }
    }
}
